import React, { FC, useContext } from "react";
import { classNames } from "../../../utils/classNames";
import { IconButton, Tooltip } from "@xapp/components";

import { INTEGRATION_CATALOG } from "../integrationCatalog";
import { IntegrationCardLayoutContext, IntegrationDetailsContext } from "../integrationCardContext";

import style from "./style.scss";

export interface IntegrationConnectionProps {
    integration: string;
}

export interface IntegrationCardProp extends Pick<BasicIntegrationCardProps, "isLinked" | "isActive" | "onDisconnect" | "disabled" | "checked" | "onClick" | "onConnect"> {
    appId: string;
}

export interface BasicIntegrationCardProps {
    /**
     * The integration's id (servicetitan, jobber, ...): looks up what it does for the row layout and
     * names it for "View details".
     */
    integrationId?: string;
    /**
     * The integration is selected and will have a check appear on top of it.
     */
    checked?: boolean;
    /**
     * The card cannot be clicked
     */
    disabled?: boolean;
    /**
     * Image URL to use
     */
    imageUrl: string;
    /**
     * Label for the card
     */
    text: string;
    /**
     * Is the integration setup
     */
    isLinked?: boolean;
    /**
     * Is the link active and working
     */
    isActive?: boolean;
    /**
     * User clicks on the icon/logo
     */
    onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void;
    /**
     * Certain integrations may have a callback when the user connects.
     *
     */
    onConnect?: (props: IntegrationConnectionProps) => void;
    /**
     * User requests to disconnect the integration.
     */
    onDisconnect?: () => void;
    /**
     * Used if there is a hyperlink that will be used to integrate, like for OAUTH.
     */
    href: () => string;
    /**
     * Optional small corner badge rendered over the logo, e.g. "TEST".
     */
    badge?: string;
}

/**
 * Most basic integration card.  This is meant to be wrapped with a dialog or other component to provide more information for a specific integration.
 */
export const BasicIntegrationCard: FC<BasicIntegrationCardProps> = ({
    integrationId,
    checked,
    disabled,
    imageUrl,
    text,
    isActive,
    isLinked,
    onDisconnect,
    onClick,
    href,
    badge
}) => {

    let tooltip = disabled ? "Disabled" : "Click to integrate";
    if (typeof checked === "boolean") {
        tooltip = checked ? "Selected" : "Click to select";
    }

    const layout = useContext(IntegrationCardLayoutContext);
    const showDetails = useContext(IntegrationDetailsContext);
    const catalog = integrationId ? INTEGRATION_CATALOG[integrationId] : undefined;

    if (layout === "row") {
        const action = typeof checked === "boolean" ? (checked ? "Selected" : "Select") : "Connect";
        return (
            <div className={classNames(style.integrationRow, { [style.disabled]: disabled })} data-integration-row>
                <div className={style.rowLogo}>
                    <img src={imageUrl} alt="" style={disabled ? { filter: "grayscale(100%)" } : undefined} />
                </div>
                <div className={style.rowBody}>
                    <h3>
                        {catalog?.name || text}
                        {badge && <span className={style.rowBadge}>{badge}</span>}
                    </h3>
                    {catalog && <p>{catalog.summary}</p>}
                    {catalog && (
                        <ul className={style.capabilities}>
                            {catalog.capabilities.map((capability) => (
                                <li key={capability} data-capability>
                                    {capability}
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
                <a
                    className={classNames(style.rowAction, { [style.rowActionSelected]: checked })}
                    onClick={disabled ? undefined : onClick}
                    href={href()}
                    aria-disabled={disabled || undefined}
                    data-connect
                >
                    {action}
                </a>
            </div>
        );
    }

    return (
        <>
            <div style={{ display: "flex", flexDirection: "column" }}>
                <Tooltip content={tooltip}>
                    <a className={classNames(style.integrationContainer, { [style.disabled]: disabled })} onClick={onClick} href={href()} >
                        <div className={classNames(style.imageContainer, { [style.checked]: checked })}>
                            <img src={imageUrl} alt={text} style={disabled ? { filter: 'grayscale(100%)' } : undefined} />
                            {badge && <div className={style.badge}>{badge}</div>}
                            {checked && <div className={style.checkmark}>✔</div>}
                        </div>
                        <p>{text}</p>
                    </a>
                </Tooltip>
                <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-evenly", alignContent: "center" }}>
                    {isLinked &&
                        <div className={style.isActiveContainer}>
                            <Tooltip content={isActive ? "Status: OK" : "Potential Issue, Reconnect"}>
                                <div className={style.isActiveIntegration} style={{ background: isActive ? "green" : "red" }}></div>
                            </Tooltip>
                            {!isActive &&
                                <Tooltip content={"Reconnect"}>
                                    <IconButton iconName="restart_alt" color="primary" />
                                </Tooltip>
                            }
                        </div>}
                    {isLinked && showDetails && integrationId &&
                        <Tooltip content={"View details"}>
                            <button
                                type="button"
                                className={style.detailsButton}
                                aria-label={`View ${catalog?.name || text} details`}
                                onClick={() => showDetails(integrationId)}
                                data-view-details
                            >
                                Details
                            </button>
                        </Tooltip>
                    }
                    {typeof onDisconnect === "function" &&
                        <Tooltip content={"Disconnect"}>
                            <IconButton iconName="close" color="warning" onClick={onDisconnect} />
                        </Tooltip>
                    }
                </div>
            </div>
        </>
    );
}